// src/components/player/PlayerAdvancedAnalytics.js — Mockup F Rev2 Implementation
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { AlertCircle, ChevronRight, ChevronDown, RefreshCw } from 'lucide-react';
import api from '../../services/apiService';
import { getCurrentSeason } from '../../utils/seasonUtils';
import { useLeague } from '../../contexts/LeagueContext';

// ═══════════════════════════════════════════════════════════════
// CONSTANTS & HELPERS
// ═══════════════════════════════════════════════════════════════

const COLORS = {
  yellow: '#eab308',
  green: '#10b981',
  blue: '#60a5fa',
  red: '#ef4444',
  gray: '#737373',
  muted: '#525252',
  darkMuted: '#404040',
  white: '#e5e5e5',
  bg: 'rgba(23,23,23,0.5)',
  border: 'rgba(55,65,81,0.5)',
};

const HEAT = {
  elite:    { bg: 'rgba(16,185,129,0.12)', color: '#10b981' },
  good:     { bg: 'rgba(96,165,250,0.08)',  color: '#60a5fa' },
  avg:      { bg: 'rgba(115,115,115,0.06)', color: '#737373' },
  below:    { bg: 'rgba(239,68,68,0.08)',   color: '#ef4444' },
};

const PANEL = {
  background: COLORS.bg,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 5,
  padding: 16,
  marginBottom: 16,
};

const SECTION_HEADER = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 1.1,
  textTransform: 'uppercase',
  color: COLORS.yellow,
  marginBottom: 10,
};

const heatTierFromPercentile = (pct) => {
  if (pct >= 80) return HEAT.elite;
  if (pct >= 60) return HEAT.good;
  if (pct >= 40) return HEAT.avg;
  return HEAT.below;
};

const heatTierFromZ = (z) => {
  if (z > 1.0) return HEAT.elite;
  if (z > 0.5) return HEAT.good;
  if (z > -0.5) return HEAT.avg;
  return HEAT.below;
};

const heatFromRank = (rank, total) => {
  if (!rank || !total) return HEAT.avg;
  const pct = ((total - rank + 1) / total) * 100;
  return heatTierFromPercentile(pct);
};

// DD-MODAL P4 fix (2026-07-15): the aggregate-z letter ladder is GONE. It graded
// off the SUM of category z-scores (a roto standings-impact magnitude) on a
// per-category ladder, which mis-graded multi-category players (Valdez: z_sum
// 0.14 → C+ while his true standing was the 87.7th percentile → A-). The only
// grade is now the percentile-based fantasy.letter_grade; a cache-miss shows an
// honest '—', never a divergent z-ladder grade.

const ordinal = (n) => {
  if (!n && n !== 0) return '—';
  const s = ['th','st','nd','rd'];
  const v = n % 100;
  return n + (s[(v-20)%10] || s[v] || s[0]);
};

const tierLabel = (pct) => {
  if (pct >= 80) return 'Elite';
  if (pct >= 60) return 'Above Avg';
  if (pct >= 40) return 'Average';
  return 'Below Avg';
};

const fmtStat = (val, cat) => {
  if (val === null || val === undefined) return '—';
  if (['AVG','OBP','SLG','OPS','WHIP'].includes(cat)) return Number(val).toFixed(3);
  if (cat === 'ERA') return Number(val).toFixed(2);
  if (cat === 'IP') return Number(val).toFixed(1);
  return typeof val === 'number' ? Math.round(val) : val;
};

// Map backend canonical z-score keys → display category abbreviations
const Z_KEY_MAP = {
  // Canonical DB field names (primary keys from backend)
  home_runs: 'HR', rbi: 'RBI', runs: 'R', stolen_bases: 'SB',
  batting_avg: 'AVG', ops: 'OPS', obp: 'OBP', slg: 'SLG',
  hits: 'H', walks: 'BB', doubles: '2B', triples: '3B', strikeouts: 'SO',
  wins: 'W', strikeouts_pitched: 'SO', saves: 'SV', quality_starts: 'QS',
  era: 'ERA', whip: 'WHIP', innings_pitched: 'IP', holds: 'HLD', losses: 'L',
};

// Map scoring category abbreviation → stats_data field names (for season/projection lookups)
const STAT_FIELD_MAP = {
  R: 'runs', HR: 'home_runs', RBI: 'rbi', SB: 'stolen_bases',
  AVG: 'batting_avg', OPS: 'ops', OBP: 'obp', SLG: 'slg',
  H: 'hits', BB: 'walks', '2B': 'doubles', '3B': 'triples',
  W: 'wins', SV: 'saves', ERA: 'era', WHIP: 'whip',
  SO: 'strikeouts_pitched', K: 'strikeouts_pitched',
  QS: 'quality_starts', IP: 'innings_pitched',
  HLD: 'holds', L: 'losses',
};

const INVERSE_CATS = new Set(['ERA', 'WHIP']);

// Default scoring categories when standings data isn't available
const DEFAULT_HITTING_CATS = ['R', 'HR', 'RBI', 'SB', 'AVG', 'OPS'];
const DEFAULT_PITCHING_CATS = ['W', 'SV', 'ERA', 'WHIP', 'SO', 'QS'];
const HITTING_CAT_SET = new Set(DEFAULT_HITTING_CATS);
const PITCHING_CAT_SET = new Set(DEFAULT_PITCHING_CATS);

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const PlayerAdvancedAnalytics = ({
  playerId,
  leagueId,
  playerName,
  isPitcher = false,
  teamId = null,
  currentMarketValue = null,
  baselinePrice = null,
  onDataLoaded = null,
}) => {
  const league = useLeague();
  const currentSeason = useMemo(
    () => league?.current_season ?? getCurrentSeason(),
    [league]
  );
  const onDataLoadedRef = useRef(onDataLoaded);
  const [advancedData, setAdvancedData] = useState(null);
  const [standingsData, setStandingsData] = useState(null);
  const [impactData, setImpactData] = useState(null);
  const [personalizedValue, setPersonalizedValue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rosExpanded, setRosExpanded] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!playerId) return;
    setLoading(true);
    setError(null);

    try {
      // Build parallel fetches
      const fetches = [
        api.players.getPlayerAdvanced(playerId, leagueId, currentSeason).catch(() => null),
      ];
      if (teamId && leagueId) {
        fetches.push(
          api.leagues.getTeamCategoryStandings(leagueId, teamId).catch(() => null),
          api.players.getPlayerFantasyImpact(leagueId, teamId, playerId).catch(() => null),
          api.leagues.getPersonalizedValue(leagueId, playerId, teamId).catch(() => null),
        );
      }

      const results = await Promise.all(fetches);

      setAdvancedData(results[0]);
      if (results.length > 1) setStandingsData(results[1]);
      if (results.length > 2) setImpactData(results[2]);
      if (results.length > 3) setPersonalizedValue(results[3]);
      if (onDataLoadedRef.current && results[0]?.overall_rankings) {
        onDataLoadedRef.current({
          overall_rankings: results[0].overall_rankings,
          position: results[0].comparison?.position_ranks?.position,
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [playerId, leagueId, currentSeason, teamId]);

  // Fetch once on mount (deps are all primitive/stable values)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, [playerId, leagueId, currentSeason, teamId]);

  // ── Derived data ──
  const fantasy = advancedData?.fantasy;
  const comparison = advancedData?.comparison?.position_ranks;
  const projections = advancedData?.projections;
  const overallRankings = advancedData?.overall_rankings;

  // Build category list from standings data (ONLY scoring categories for this player type)
  // Falls back to defaults if standings endpoint didn't load
  const categories = useMemo(() => {
    const typeSet = isPitcher ? PITCHING_CAT_SET : HITTING_CAT_SET;
    // Best source: category-standings endpoint returns scoring categories for both types
    // Filter to only the categories relevant to this player's type
    if (standingsData?.categories && Object.keys(standingsData.categories).length > 0) {
      const filtered = Object.keys(standingsData.categories).filter(c => typeSet.has(c));
      if (filtered.length > 0) return filtered;
    }
    // Fallback: use defaults based on player type
    return isPitcher ? DEFAULT_PITCHING_CATS : DEFAULT_HITTING_CATS;
  }, [standingsData, isPitcher]);

  // Z-score lookup: normalized to uppercase category keys
  const zScores = useMemo(() => {
    if (!fantasy?.category_z_scores) return {};
    const out = {};
    for (const [k, v] of Object.entries(fantasy.category_z_scores)) {
      out[Z_KEY_MAP[k] || k.toUpperCase()] = v;
    }
    return out;
  }, [fantasy]);

  // Comparison data lookup helpers
  const leagueRanks = useMemo(() => {
    if (!comparison?.league?.category_ranks) return {};
    const out = {};
    for (const [k, v] of Object.entries(comparison.league.category_ranks)) {
      out[Z_KEY_MAP[k] || k.toUpperCase()] = v;
    }
    return out;
  }, [comparison]);

  const leaguePercentiles = useMemo(() => {
    if (!comparison?.league?.percentiles) return {};
    const out = {};
    for (const [k, v] of Object.entries(comparison.league.percentiles)) {
      out[Z_KEY_MAP[k] || k.toUpperCase()] = v;
    }
    return out;
  }, [comparison]);

  const mlbRanks = useMemo(() => {
    const src = comparison?.mlb?.category_ranks || comparison?.category_ranks || {};
    const out = {};
    for (const [k, v] of Object.entries(src)) {
      out[Z_KEY_MAP[k] || k.toUpperCase()] = v;
    }
    return out;
  }, [comparison]);

  const mlbPercentiles = useMemo(() => {
    const src = comparison?.mlb?.percentiles || comparison?.percentiles || {};
    const out = {};
    for (const [k, v] of Object.entries(src)) {
      out[Z_KEY_MAP[k] || k.toUpperCase()] = v;
    }
    return out;
  }, [comparison]);

  const leagueTotal = comparison?.league?.total_players || 0;
  const mlbTotal = comparison?.mlb?.total_players || comparison?.total_players || 0;

  // Projection data (season totals + ROS) — uses long-form DB field names
  // Service already unwraps stats_snapshot, so projections = {stats_data: {hitting: {...}}}
  const projStatsRaw = useMemo(() => {
    const statsData = projections?.stats_data;
    if (!statsData) return {};
    const side = isPitcher ? statsData.pitching : statsData.hitting;
    // §9: full-season reads projected_full ONLY (no talent fallback). Missing → empty object →
    // each value renders "—", so a broken daily refresh is visible, not masked by the talent total.
    return side?.projected_full || {};
  }, [projections, isPitcher]);

  // Current season stats from projections snapshot — uses long-form DB field names
  const currentStatsRaw = useMemo(() => {
    const statsData = projections?.stats_data;
    if (!statsData) return {};
    const side = isPitcher ? statsData.pitching : statsData.hitting;
    return side?.current || {};
  }, [projections, isPitcher]);

  // Map long-form field names to category abbreviations for heatmap lookup
  const mapRawToCategories = useCallback((raw) => {
    const mapped = {};
    for (const [abbrev, field] of Object.entries(STAT_FIELD_MAP)) {
      if (raw[field] !== undefined) mapped[abbrev] = raw[field];
      if (raw[abbrev] !== undefined) mapped[abbrev] = raw[abbrev]; // also check abbrev directly
    }
    return mapped;
  }, []);

  const projStats = useMemo(() => mapRawToCategories(projStatsRaw), [projStatsRaw, mapRawToCategories]);
  const currentStats = useMemo(() => mapRawToCategories(currentStatsRaw), [currentStatsRaw, mapRawToCategories]);

  // Strengths & watch areas
  const { strengths, watchAreas } = useMemo(() => {
    const entries = categories.map(cat => ({
      cat,
      z: zScores[cat] || 0,
      pct: leaguePercentiles[cat] || (mlbPercentiles[cat]) || 50,
      rank: leagueRanks[cat] || mlbRanks[cat] || null,
    }));
    const sorted = [...entries].sort((a, b) => b.z - a.z);
    return {
      strengths: sorted.filter(e => e.pct >= 60).slice(0, 3),
      watchAreas: sorted.filter(e => e.pct < 60).slice(-3).reverse(),
    };
  }, [categories, zScores, leaguePercentiles, mlbPercentiles, leagueRanks, mlbRanks]);

  // ── Loading ──
  if (loading) {
    return (
      <div style={{ ...PANEL, textAlign: 'center', padding: 40 }}>
        <RefreshCw size={24} style={{ color: COLORS.yellow, animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
        <div style={{ color: COLORS.gray }}>Loading advanced analytics...</div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <div style={{ ...PANEL, textAlign: 'center', padding: 40 }}>
        <AlertCircle size={28} style={{ color: COLORS.red, margin: '0 auto 8px' }} />
        <div style={{ color: COLORS.red, marginBottom: 12 }}>{error}</div>
        <button
          onClick={fetchAll}
          style={{
            background: COLORS.yellow, color: '#000', border: 'none', borderRadius: 4,
            padding: '6px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13,
          }}
        >Retry</button>
      </div>
    );
  }

  // ── No data or cache miss ──
  if (!advancedData || !fantasy || fantasy.error) {
    return (
      <div style={{ ...PANEL, textAlign: 'center', padding: 40 }}>
        <div style={{ color: COLORS.gray }}>Not enough data to generate analytics</div>
      </div>
    );
  }

  // DEFECT-3 fix (2026-07-14): the grade is the PERCENTILE-based standing the
  // service computes (fantasy.letter_grade), NOT letterGrade(aggregate_z_score).
  // `aggregate_z_score` is the SUM of the league's scoring-category z-scores (a
  // roto standings-impact magnitude) — feeding that SUM into a per-category
  // z-ladder cratered multi-category players (Valdez: 5-cat z_sum 0.14 → C+,
  // while his true standing is the 87.7th percentile → A-). Small sample gets a
  // caveat, never a grade computed off the summed z.
  const pctile = fantasy.percentile || 0;
  // The grade is ONLY the percentile-based fantasy.letter_grade (no z-sum
  // fallback — DD-MODAL P4). Cache-miss → honest '—'/gray, matching the
  // Fantasy Value tile, so Overall Grade and Fantasy Value can never disagree.
  const grade = fantasy.letter_grade || '—';
  const gradeColor = fantasy.letter_grade ? heatTierFromPercentile(pctile).color : COLORS.gray;

  // Market Value: signed delta vs baseline. Drives color + ▲/▼ on Tile 4.
  const mvChange = (currentMarketValue != null && baselinePrice != null)
    ? currentMarketValue - baselinePrice
    : null;

  // Personalized Fantasy Value — letter grade color from the 0-100 score.
  const pvGradeColor = (score) => {
    if (score == null) return COLORS.gray;
    if (score >= 80) return COLORS.green;
    if (score >= 60) return COLORS.blue;
    if (score >= 40) return COLORS.yellow;
    return COLORS.red;
  };

  // C44 correction (2026-07-14): the optimizer swap-fit score STAYS as a feature
  // but under its honest label — this tile is now 'Roster Fit', never 'Fantasy
  // Value'. Three-row layout matches the other strip tiles:
  //   Row 1 (height 28px): grade letter + score (baseline-aligned, flex)
  //   Row 2 (height 14px): subline — "Best fit: POS · vs Incumbent"
  //   Row 3: SECTION_HEADER "Roster Fit"
  // Gains/costs intentionally omitted — those live in the full FantasyImpact panel.
  const RosterFitTile = ({ pv }) => {
    const subLine = pv
      ? (pv.status === 'on_your_roster'
          ? 'On your roster'
          : pv.replaces?.player_name
            ? `Best fit: ${pv.best_slot || '—'} · vs ${pv.replaces.player_name}`
            : pv.best_slot
              ? `Best fit: ${pv.best_slot}`
              : 'No eligible slot')
      : '';

    return (
      <div style={{ ...tileStyle, flex: '1 1 0' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 6 }}>
          {pv ? (
            <>
              <span style={{ fontSize: 28, fontWeight: 800, color: pvGradeColor(pv.score) }}>
                {pv.grade || '—'}
              </span>
              <span style={{ fontSize: 20, fontWeight: 700, color: COLORS.white }}>
                {pv.score != null ? pv.score : ''}
              </span>
            </>
          ) : (
            <span style={{ fontSize: 28, fontWeight: 800, color: COLORS.gray }}>—</span>
          )}
        </div>
        <div style={{
          fontSize: 10,
          color: COLORS.muted,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minHeight: 14,
        }}>
          {subLine || '\u00A0'}
        </div>
        <div style={{ ...SECTION_HEADER, marginBottom: 0, marginTop: 4 }}>Roster Fit</div>
      </div>
    );
  };

  // C44 correction (2026-07-14): the 'Fantasy Value' tile = the TRUE
  // FantasyService grade for EVERY player — qualified players ranked as usual,
  // unqualified-active players carry the small_sample-flagged grade
  // (fantasy.small_sample / fantasy.percentile_caveat: measured vs the
  // qualified pool) with the caveat in the sub-line. Same fields as the
  // AnalyticsGrid value cell.
  const smallSample = fantasy.small_sample === true;
  const TrueValueTile = () => (
    <div style={{ ...tileStyle, flex: '1 1 0' }}>
      {/* B1 (2026-07-14): LETTER GRADE headline only — never a dollar figure
          (price is the Market Value tile's job). */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 6 }}>
        <span style={{ fontSize: 28, fontWeight: 800, color: fantasy.letter_grade ? heatTierFromPercentile(pctile).color : COLORS.gray }}>
          {fantasy.letter_grade || '—'}
        </span>
      </div>
      <div
        style={{
          fontSize: 10,
          color: smallSample ? COLORS.yellow : COLORS.muted,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minHeight: 14,
        }}
        title={smallSample ? (fantasy.percentile_caveat || undefined) : undefined}
      >
        {fantasy.letter_grade
          ? (smallSample ? 'small sample · vs qualified pool' : `${ordinal(Math.floor(pctile))} percentile`)
          : 'not enough games for a grade'}
      </div>
      <div style={{ ...SECTION_HEADER, marginBottom: 0, marginTop: 4 }}>Fantasy Value</div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════
  // SECTION A: Summary Strip
  // ═══════════════════════════════════════════════════════════════
  const SummaryStrip = () => (
    <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
      {/* Tile 1: Overall Grade — DEFECT-3: grade is the percentile standing; the
          sub shows that standing (or the small-sample caveat), NOT the summed z
          that used to mis-grade it. */}
      <div style={{ ...tileStyle, flex: '1 1 0' }}>
        <div style={{ fontSize: 28, fontWeight: 800, color: gradeColor }}>{grade}</div>
        <div style={{ fontSize: 11, color: smallSample ? COLORS.yellow : COLORS.muted }}
             title={smallSample ? (fantasy.percentile_caveat || undefined) : undefined}>
          {smallSample ? 'small sample' : `${ordinal(Math.floor(pctile))} pctl`}
        </div>
        <div style={{ ...SECTION_HEADER, marginBottom: 0, marginTop: 4 }}>Overall Grade</div>
      </div>
      {/* Tile 2: Percentile */}
      <div style={{ ...tileStyle, flex: '1 1 0' }}>
        <div style={{ fontSize: 28, fontWeight: 800, color: heatTierFromPercentile(pctile).color }}>
          {ordinal(Math.floor(pctile))}
        </div>
        <div style={{ fontSize: 11, color: COLORS.muted }}>{tierLabel(pctile)}</div>
        <div style={{ ...SECTION_HEADER, marginBottom: 0, marginTop: 4 }}>Percentile</div>
      </div>
      {/* Tile 3: Fantasy Value — the TRUE FantasyService grade (flagged-caveat
          aware). C44 correction: the pv swap score no longer wears this label. */}
      <TrueValueTile />
      {/* Tile 3b: Roster Fit (personalized swap analysis, compact tile form,
          honest label). Data: RosterOptimizerService.calculate_personalized_value
          — runs MoveScorer.score_move_fast over each eligible roster slot and
          picks the best weighted_score. The strip flex-wraps; six tiles is fine. */}
      <RosterFitTile pv={personalizedValue} />
      {/* Tile 4: Market Value — reads current_market_value from player_prices (SSOT),
          compared against baseline_price. Previously rendered fantasy.dollar_value, which
          is a separate live computation and produced a different number than Overview. */}
      <div style={{ ...tileStyle, flex: '1 1 0' }}>
        <div style={{ fontSize: 28, fontWeight: 800, color: mvChange > 0 ? COLORS.green : (mvChange < 0 ? COLORS.red : COLORS.white) }}>
          {currentMarketValue != null ? `$${Math.round(currentMarketValue)}` : '—'}
        </div>
        <div style={{ fontSize: 11, color: mvChange > 0 ? COLORS.green : (mvChange < 0 ? COLORS.red : COLORS.muted) }}>
          {mvChange != null ? `${mvChange > 0 ? '▲' : mvChange < 0 ? '▼' : ''} $${Math.abs(Math.round(mvChange))} vs base` : (baselinePrice != null ? `$${Math.round(baselinePrice)} base` : '—')}
        </div>
        <div style={{ ...SECTION_HEADER, marginBottom: 0, marginTop: 4 }}>Market Value</div>
      </div>
      {/* Tile 5: Overall League Rank */}
      <div style={{ ...tileStyle, flex: '1 1 0' }}>
        <div style={{ fontSize: 28, fontWeight: 800, color: COLORS.blue }}>
          {overallRankings?.league?.rank ? `#${overallRankings.league.rank}` : (overallRankings?.mlb?.rank ? `#${overallRankings.mlb.rank}` : 'Calc...')}
        </div>
        <div style={{ fontSize: 11, color: COLORS.muted }}>
          {comparison?.position || ''} · of {overallRankings?.league?.total_players || overallRankings?.mlb?.total_players || '—'}
        </div>
        <div style={{ ...SECTION_HEADER, marginBottom: 0, marginTop: 4 }}>Overall Rank</div>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════
  // SECTION B: Category Heatmap Matrix
  // ═══════════════════════════════════════════════════════════════
  const HeatmapMatrix = () => {
    if (categories.length === 0) {
      return (
        <div style={{ ...PANEL, textAlign: 'center' }}>
          <div style={{ color: COLORS.gray }}>No category data available.</div>
        </div>
      );
    }

    const cellStyle = (heat) => ({
      padding: '6px 10px',
      textAlign: 'center',
      fontSize: 13,
      fontWeight: 600,
      background: heat.bg,
      color: heat.color,
      borderBottom: `1px solid ${COLORS.border}`,
    });

    const headerCell = {
      padding: '6px 10px',
      textAlign: 'center',
      fontSize: 11,
      fontWeight: 700,
      color: COLORS.yellow,
      borderBottom: `2px solid ${COLORS.border}`,
    };

    const rowLabel = (text, style = {}) => ({
      padding: '6px 10px',
      fontSize: 12,
      fontWeight: 600,
      color: COLORS.white,
      borderBottom: `1px solid ${COLORS.border}`,
      whiteSpace: 'nowrap',
      ...style,
    });

    // Helper to get stat value — tries category abbreviation first, then DB field name
    const getStatValue = (cat, source) => {
      // Try direct category abbreviation (e.g. 'HR', 'AVG')
      if (source[cat] !== undefined) return source[cat];
      // Try lowercase abbreviation
      if (source[cat.toLowerCase()] !== undefined) return source[cat.toLowerCase()];
      // Try the long-form DB field name (e.g. 'home_runs' for 'HR')
      const field = STAT_FIELD_MAP[cat];
      if (field && source[field] !== undefined) return source[field];
      return null;
    };

    return (
      <div style={PANEL}>
        <div style={SECTION_HEADER}>Category Heatmap</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: categories.length * 80 + 120 }}>
            <thead>
              <tr>
                <th style={{ ...headerCell, textAlign: 'left', width: 120 }}></th>
                {categories.map(cat => (
                  <th key={cat} style={headerCell}>{cat}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Row 1: Season (Current) */}
              <tr>
                <td style={rowLabel('Season')}>Season</td>
                {categories.map(cat => {
                  const val = getStatValue(cat, currentStats);
                  const pct = leaguePercentiles[cat] || mlbPercentiles[cat] || 50;
                  return (
                    <td key={cat} style={cellStyle(heatTierFromPercentile(pct))}>
                      {fmtStat(val, cat)}
                    </td>
                  );
                })}
              </tr>

              {/* Row 2: Total Projection (clickable) */}
              <tr
                onClick={() => setRosExpanded(!rosExpanded)}
                style={{ cursor: 'pointer' }}
              >
                <td style={rowLabel('Proj', { color: COLORS.yellow })}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {rosExpanded
                      ? <ChevronDown size={14} style={{ color: COLORS.yellow }} />
                      : <ChevronRight size={14} style={{ color: COLORS.yellow }} />}
                    Total Proj
                  </span>
                </td>
                {categories.map(cat => {
                  const val = getStatValue(cat, projStats);
                  const pct = leaguePercentiles[cat] || mlbPercentiles[cat] || 50;
                  return (
                    <td key={cat} style={{ ...cellStyle(heatTierFromPercentile(pct)), color: COLORS.yellow }}>
                      {fmtStat(val, cat)}
                    </td>
                  );
                })}
              </tr>

              {/* Row 3: ROS Remaining (hidden by default) */}
              {rosExpanded && (
                <tr>
                  <td style={rowLabel('ROS', { color: COLORS.blue, fontStyle: 'italic', paddingLeft: 24 })}>
                    ↳ ROS
                  </td>
                  {categories.map(cat => {
                    const proj = getStatValue(cat, projStats);
                    const cur = getStatValue(cat, currentStats);
                    const delta = (proj !== null && cur !== null) ? proj - cur : null;
                    return (
                      <td key={cat} style={{
                        ...cellStyle(HEAT.good),
                        color: COLORS.blue,
                        fontStyle: 'italic',
                        background: 'rgba(96,165,250,0.06)',
                      }}>
                        {delta !== null ? (INVERSE_CATS.has(cat) ? fmtStat(delta, cat) : `+${fmtStat(delta, cat)}`) : '—'}
                      </td>
                    );
                  })}
                </tr>
              )}

              {/* Divider */}
              <tr>
                <td colSpan={categories.length + 1} style={{ height: 2, background: COLORS.border, padding: 0 }} />
              </tr>

              {/* Row: Z-Score */}
              <tr>
                <td style={rowLabel('Z-Score')}>Z-Score</td>
                {categories.map(cat => {
                  const z = zScores[cat];
                  return (
                    <td key={cat} style={cellStyle(z !== undefined ? heatTierFromZ(z) : HEAT.avg)}>
                      {z !== undefined ? (z >= 0 ? '+' : '') + z.toFixed(2) : '—'}
                    </td>
                  );
                })}
              </tr>

              {/* Row: League Rank */}
              {leagueTotal > 0 && (
                <tr>
                  <td style={rowLabel('Lg Rank')}>Lg Rank</td>
                  {categories.map(cat => {
                    const rank = leagueRanks[cat];
                    return (
                      <td key={cat} style={cellStyle(heatFromRank(rank, leagueTotal))}>
                        {rank ? `#${rank}` : '—'}
                        {rank && leagueTotal ? <span style={{ fontSize: 9, opacity: 0.6, display: 'block' }}>of {leagueTotal}</span> : null}
                      </td>
                    );
                  })}
                </tr>
              )}

              {/* Row: League %ile */}
              {leagueTotal > 0 && (
                <tr>
                  <td style={rowLabel('Lg %ile')}>Lg %ile</td>
                  {categories.map(cat => {
                    const pct = leaguePercentiles[cat];
                    return (
                      <td key={cat} style={cellStyle(pct !== undefined ? heatTierFromPercentile(pct) : HEAT.avg)}>
                        {pct !== undefined ? `${Math.round(pct)}%` : '—'}
                      </td>
                    );
                  })}
                </tr>
              )}

              {/* Row: MLB Rank */}
              <tr>
                <td style={rowLabel('MLB Rank')}>MLB Rank</td>
                {categories.map(cat => {
                  const rank = mlbRanks[cat];
                  return (
                    <td key={cat} style={cellStyle(heatFromRank(rank, mlbTotal))}>
                      {rank ? `#${rank}` : '—'}
                      {rank && mlbTotal ? <span style={{ fontSize: 9, opacity: 0.6, display: 'block' }}>of {mlbTotal}</span> : null}
                    </td>
                  );
                })}
              </tr>

              {/* Row: MLB %ile */}
              <tr>
                <td style={rowLabel('MLB %ile')}>MLB %ile</td>
                {categories.map(cat => {
                  const pct = mlbPercentiles[cat];
                  return (
                    <td key={cat} style={cellStyle(pct !== undefined ? heatTierFromPercentile(pct) : HEAT.avg)}>
                      {pct !== undefined ? `${Math.round(pct)}%` : '—'}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 16, marginTop: 10, justifyContent: 'center' }}>
          {[
            { label: 'Elite (≥80%)', ...HEAT.elite },
            { label: 'Above Avg (60-79%)', ...HEAT.good },
            { label: 'Average (40-59%)', ...HEAT.avg },
            { label: 'Below Avg (<40%)', ...HEAT.below },
          ].map(t => (
            <div key={t.label} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: COLORS.muted }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.color, display: 'inline-block' }} />
              {t.label}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  // SECTION C: Strengths & Watch Areas
  // ═══════════════════════════════════════════════════════════════
  const StrengthsWatch = () => (
    <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
      {/* Strengths */}
      <div style={{ ...PANEL, flex: '1 1 280px', marginBottom: 0 }}>
        <div style={{ ...SECTION_HEADER, color: COLORS.green }}>▲ Strengths</div>
        {strengths.length === 0 ? (
          <div style={{ color: COLORS.muted, fontSize: 12 }}>No standout categories</div>
        ) : strengths.map(s => (
          <div key={s.cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${COLORS.border}` }}>
            <span style={{ color: COLORS.white, fontWeight: 600, fontSize: 13 }}>{s.cat}</span>
            <span style={{ fontSize: 12 }}>
              <span style={{ color: COLORS.green, marginRight: 8 }}>z: {s.z.toFixed(2)}</span>
              <span style={{ color: COLORS.muted }}>
                {s.rank ? `#${s.rank}` : ''}
                {s.pct ? ` · ${Math.round(s.pct)}%ile` : ''}
              </span>
            </span>
          </div>
        ))}
      </div>
      {/* Watch Areas */}
      <div style={{ ...PANEL, flex: '1 1 280px', marginBottom: 0 }}>
        <div style={{ ...SECTION_HEADER, color: COLORS.yellow }}>◆ Watch Areas</div>
        {watchAreas.length === 0 ? (
          <div style={{ color: COLORS.muted, fontSize: 12 }}>No weak categories</div>
        ) : watchAreas.map(w => (
          <div key={w.cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${COLORS.border}` }}>
            <span style={{ color: COLORS.white, fontWeight: 600, fontSize: 13 }}>{w.cat}</span>
            <span style={{ fontSize: 12 }}>
              <span style={{ color: COLORS.red, marginRight: 8 }}>z: {w.z.toFixed(2)}</span>
              <span style={{ color: COLORS.muted }}>
                {w.rank ? `#${w.rank}` : ''}
                {w.pct ? ` · ${Math.round(w.pct)}%ile` : ''}
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════
  // SECTION D: Fantasy Impact — Team Contribution
  // ═══════════════════════════════════════════════════════════════
  const FantasyImpact = () => {
    if (!teamId || !standingsData || !impactData) return null;

    const standingsCats = standingsData?.categories || {};
    const impactCats = impactData?.categories || {};
    const totalTeams = standingsData?.total_teams || 10;

    // Determine which categories to show from standings data (filtered by player type)
    const typeSet = isPitcher ? PITCHING_CAT_SET : HITTING_CAT_SET;
    const impactCategories = Object.keys(standingsCats).length > 0
      ? Object.keys(standingsCats).filter(c => typeSet.has(c))
      : categories;

    if (impactCategories.length === 0) return null;

    const rankColor = (rank) => {
      if (!rank) return COLORS.gray;
      const topThird = Math.ceil(totalTeams / 3);
      const bottomThird = totalTeams - topThird;
      if (rank <= topThird) return COLORS.green;
      if (rank <= bottomThird) return COLORS.yellow;
      return COLORS.red;
    };


    const headerCell = {
      padding: '6px 10px', textAlign: 'center', fontSize: 11, fontWeight: 700,
      color: COLORS.yellow, borderBottom: `2px solid ${COLORS.border}`,
    };

    return (
      <div style={PANEL}>
        <div style={SECTION_HEADER}>Fantasy Impact — Team Contribution</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: impactCategories.length * 80 + 120 }}>
            <thead>
              <tr>
                <th style={{ ...headerCell, textAlign: 'left', width: 120 }}></th>
                {impactCategories.map(cat => (
                  <th key={cat} style={headerCell}>{cat}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Row 1: Team Rank */}
              <tr>
                <td style={{ padding: '6px 10px', fontSize: 12, fontWeight: 600, color: COLORS.white, borderBottom: `1px solid ${COLORS.border}` }}>
                  Team Rank
                </td>
                {impactCategories.map(cat => {
                  const rank = standingsCats[cat]?.rank;
                  return (
                    <td key={cat} style={{
                      padding: '6px 10px', textAlign: 'center', fontSize: 13, fontWeight: 700,
                      color: rankColor(rank), borderBottom: `1px solid ${COLORS.border}`,
                    }}>
                      {rank ? `#${rank}` : '—'}
                    </td>
                  );
                })}
              </tr>

              {/* Row 2: Pts Impact */}
              <tr>
                <td style={{ padding: '6px 10px', fontSize: 12, fontWeight: 600, color: COLORS.white, borderBottom: `1px solid ${COLORS.border}` }}>
                  Pts Impact
                </td>
                {impactCategories.map(cat => {
                  const catImpact = impactCats[cat];
                  if (!catImpact) {
                    return (
                      <td key={cat} style={{ padding: '6px 10px', textAlign: 'center', fontSize: 13, color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}` }}>
                        —
                      </td>
                    );
                  }
                  const pts = -(catImpact.roto_points_change ?? 0);
                  const ptsColor = pts > 0 ? COLORS.green : pts < 0 ? COLORS.red : COLORS.gray;
                  const ptsDisplay = pts > 0 ? `+${pts}` : `${pts}`;
                  return (
                    <td key={cat} style={{ padding: '6px 10px', textAlign: 'center', fontSize: 13, fontWeight: 600, color: ptsColor, borderBottom: `1px solid ${COLORS.border}` }}>
                      {ptsDisplay}
                    </td>
                  );
                })}
              </tr>

              {/* Row 3: Net Impact */}
              <tr>
                <td style={{ padding: '6px 10px', fontSize: 12, fontWeight: 600, color: COLORS.white, borderBottom: `1px solid ${COLORS.border}` }}>
                  Net Impact
                </td>
                {impactCategories.map(cat => {
                  const catImpact = impactCats[cat];
                  const impact = catImpact?.impact ?? 'neutral';
                  let label, color;
                  if (impact === 'helping') { label = '▲ Helping'; color = COLORS.green; }
                  else if (impact === 'hurting') { label = '▼ Hurting'; color = COLORS.red; }
                  else { label = '— Neutral'; color = COLORS.yellow; }
                  return (
                    <td key={cat} style={{
                      padding: '6px 10px', textAlign: 'center', fontSize: 12, fontWeight: 600,
                      color, borderBottom: `1px solid ${COLORS.border}`,
                    }}>
                      {label}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 10, color: COLORS.darkMuted, marginTop: 8, lineHeight: 1.5 }}>
          Impact = what happens to team rank if player is removed · ▲ = team drops (player helping) · ▼ = team improves (player hurting) · Only shown for owned players
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <div>
      <SummaryStrip />
      <HeatmapMatrix />
      <StrengthsWatch />
      <FantasyImpact />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// SHARED STYLES
// ═══════════════════════════════════════════════════════════════

const tileStyle = {
  background: COLORS.bg,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 5,
  padding: '12px 8px',
  textAlign: 'center',
  minWidth: 100,
};

export default React.memo(PlayerAdvancedAnalytics);
